import { useMemo, useState } from 'react';

import type { ArInvoiceListRow, ChartOfAccount, CreateArReceiptInput } from '@dcc-erp/shared';
import {
  CreateArReceiptSchema,
  dateToInputValue,
  fmt,
  toNumber,
  WHT_INCOME_TYPES,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import { useArInvoices, useCoa, useCreateArReceipt, useCustomers } from '../../lib/queries';
import { SearchSelect } from '../SearchSelect';
import { modalBody, modalError, modalFooter, modalLabel } from './modal-style';
import { allocationError, totalAllocated } from './receipt-allocation';
import { AppModal } from '../AppModal';

// =============================================================================
// Customer-first multi-invoice AR receipt.
//
// RecordReceiptModal (container) owns customer selection + data fetching; the
// inner RecordReceiptForm (presentational, router/data-free) owns the per-row
// allocation grid + the cheque-vs-allocated bar so the submit shape is unit
// testable without a query client. The API already validates per-invoice
// outstanding + dimension, so the form's guard (allocationError) is a UX gate.
// =============================================================================

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100;
}

// Income-type options for the per-row WHT control — keyed by the shared preset
// table so the rate is sourced from one place (s.50bis defaults, editable).
const whtIncomeOptions = Object.entries(WHT_INCOME_TYPES).map(([value, { label }]) => ({
  value,
  label,
}));

// Minimal shapes the form actually reads — accept the full list/COA rows but
// only depend on these fields so callers (and tests) can pass lean fixtures.
// `subtotal` is needed to compute the pre-VAT base WHT is withheld on.
type OpenInvoice = Pick<
  ArInvoiceListRow,
  'id' | 'voucherNo' | 'totalAmount' | 'paidAmount' | 'creditedAmount' | 'subtotal' | 'dueDate'
>;
type CashAccount = Pick<ChartOfAccount, 'code' | 'nameTh'>;

export interface RecordReceiptFormProps {
  openInvoices: OpenInvoice[];
  cashAccounts: CashAccount[];
  onSubmit: (payload: CreateArReceiptInput) => void;
  submitting: boolean;
  // Container-supplied; the customer is already chosen upstream. Defaults to ''
  // so the presentational form can be rendered in isolation (tests assert only
  // the allocations array).
  customerId?: string;
  // Invoice ids to pre-tick on mount (per-row / bulk "รับชำระ" preset). Each
  // pre-ticked row seeds its amount to that invoice's outstanding. The container
  // keys the form per open so this lazy seed re-runs each time the modal opens.
  presetInvoiceIds?: string[];
}

export function RecordReceiptForm({
  openInvoices,
  cashAccounts,
  onSubmit,
  submitting,
  customerId = '',
  presetInvoiceIds = [],
}: RecordReceiptFormProps) {
  const today = new Date();
  const [receiptDate, setReceiptDate] = useState(dateToInputValue(today));
  const [cashAccountCode, setCashAccountCode] = useState<string>(cashAccounts[0]?.code ?? '1112');
  const [description, setDescription] = useState('');

  // Per-invoice tick + amount. A ticked row defaults its amount to that
  // invoice's outstanding; unticking drops it from the submitted array.
  // Lazy initialisers seed any preset invoice ids (that are actually open) as
  // ticked + amount=outstanding so a per-row / bulk preset pre-fills the grid
  // without the user re-selecting. Keyed per open via the container's form key.
  const [ticked, setTicked] = useState<Record<string, boolean>>(() => {
    const seed: Record<string, boolean> = {};
    const open = new Set(openInvoices.map((inv) => inv.id));
    for (const id of presetInvoiceIds) if (open.has(id)) seed[id] = true;
    return seed;
  });
  const [amounts, setAmounts] = useState<Record<string, number>>(() => {
    const seed: Record<string, number> = {};
    for (const inv of openInvoices) {
      if (presetInvoiceIds.includes(inv.id)) {
        seed[inv.id] =
          toNumber(inv.totalAmount) - toNumber(inv.paidAmount) - toNumber(inv.creditedAmount);
      }
    }
    return seed;
  });

  // Per-invoice WHT — set when the clerk picks an income type. amount is the
  // computed (overridable) withheld figure; rate + incomeType are echoed into
  // the allocation. A row with no income type carries no WHT (0).
  const [wht, setWht] = useState<
    Record<string, { amount: number; rate: number; incomeType: string }>
  >({});
  // Receipt-level 50-ทวิ cert reference (optional). When filled the created
  // certs are RECEIVED; otherwise PENDING (chased later).
  const [whtCertNo, setWhtCertNo] = useState('');
  const [whtCertDate, setWhtCertDate] = useState('');

  // Pre-VAT base WHT is withheld on for a given gross allocation: the gross
  // scaled by the invoice's pre-VAT proportion (subtotal / totalAmount).
  function preVatBase(inv: OpenInvoice, gross: number): number {
    const total = toNumber(inv.totalAmount);
    if (total <= 0) return 0;
    return round2((gross * toNumber(inv.subtotal)) / total);
  }

  const outstandingById = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      // outstanding = total − paid − credited (Phase 2b; credited reads 0 today).
      m[inv.id] =
        toNumber(inv.totalAmount) - toNumber(inv.paidAmount) - toNumber(inv.creditedAmount);
    }
    return m;
  }, [openInvoices]);

  function toggle(invId: string) {
    setTicked((prev) => {
      const next = { ...prev, [invId]: !prev[invId] };
      return next;
    });
    setAmounts((prev) => {
      if (prev[invId] !== undefined) return prev;
      // First tick defaults to the full outstanding.
      return { ...prev, [invId]: outstandingById[invId] ?? 0 };
    });
  }

  function setAmount(invId: string, v: number) {
    setAmounts((prev) => ({ ...prev, [invId]: v }));
  }

  // Income-type change: blank clears WHT for the row; a preset seeds the rate
  // and computes the withheld amount on the pre-VAT base of the current gross.
  function setIncomeType(inv: OpenInvoice, incomeType: string) {
    setWht((prev) => {
      if (!incomeType) {
        const next = { ...prev };
        delete next[inv.id];
        return next;
      }
      const rate = WHT_INCOME_TYPES[incomeType as keyof typeof WHT_INCOME_TYPES].defaultRate;
      const gross = amounts[inv.id] ?? 0;
      const amount = round2((preVatBase(inv, gross) * rate) / 100);
      return { ...prev, [inv.id]: { amount, rate, incomeType } };
    });
  }

  // Manual override of the computed withheld figure; keeps the chosen rate +
  // income type so the cert still records why it was withheld.
  function setWhtAmount(invId: string, v: number) {
    setWht((prev) => {
      const cur = prev[invId];
      if (!cur) return prev;
      return { ...prev, [invId]: { ...cur, amount: v } };
    });
  }

  // Effective allocations: only ticked rows, keyed for the helpers.
  const allocMap = useMemo(() => {
    const m: Record<string, number> = {};
    for (const inv of openInvoices) {
      if (ticked[inv.id]) m[inv.id] = amounts[inv.id] ?? 0;
    }
    return m;
  }, [openInvoices, ticked, amounts]);

  const allocatedOutstanding = useMemo(() => {
    const m: Record<string, number> = {};
    for (const id of Object.keys(allocMap)) m[id] = outstandingById[id] ?? 0;
    return m;
  }, [allocMap, outstandingById]);

  const allocated = totalAllocated(allocMap);
  const error = allocationError(allocMap, allocatedOutstanding);
  const tickedCount = Object.keys(allocMap).length;

  // WHT withheld across the ticked + positive rows, and the resulting net cash
  // the customer actually pays (gross − WHT). Only rows still in the submitted
  // set count toward the bar.
  const whtTotal = useMemo(() => {
    let sum = 0;
    for (const inv of openInvoices) {
      if (ticked[inv.id] && (amounts[inv.id] ?? 0) > 0) sum += wht[inv.id]?.amount ?? 0;
    }
    return round2(sum);
  }, [openInvoices, ticked, amounts, wht]);
  const netCash = round2(allocated - whtTotal);

  // Only ticked rows that actually carry a positive amount are submittable; a
  // ticked-but-zero row would yield {amount: 0} which the API rejects, so it is
  // dropped from the payload AND gates the submit button (alongside the
  // existing allocationError check). WHT fields are appended only when an
  // income type is chosen so a no-WHT receipt keeps its lean {invoiceId,amount}.
  const allocations = useMemo(
    () =>
      openInvoices
        .filter((inv) => ticked[inv.id] && (amounts[inv.id] ?? 0) > 0)
        .map((inv) => {
          const base = { invoiceId: inv.id, amount: amounts[inv.id] ?? 0 };
          const row = wht[inv.id];
          if (!row) return base;
          return {
            ...base,
            whtAmount: row.amount,
            whtRate: row.rate,
            incomeType: row.incomeType,
          };
        }),
    [openInvoices, ticked, amounts, wht],
  );

  const disabled =
    !cashAccountCode ||
    tickedCount === 0 ||
    allocations.length === 0 ||
    error !== null ||
    submitting;

  function handleSubmit() {
    const payload: CreateArReceiptInput = {
      receiptDate,
      customerId,
      cashAccountCode,
      ...(description ? { description } : {}),
      ...(whtCertNo ? { whtCertNo } : {}),
      ...(whtCertDate ? { whtCertDate } : {}),
      allocations,
    };
    onSubmit(payload);
  }

  return (
    <div style={modalBody}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <div>
          <label style={modalLabel}>วันที่รับชำระ</label>
          <input
            type="date"
            className="input"
            value={receiptDate}
            onChange={(e) => setReceiptDate(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          />
        </div>
        <div>
          <label style={modalLabel}>บัญชีเงินสด / ธนาคาร</label>
          <select
            className="select"
            value={cashAccountCode}
            onChange={(e) => setCashAccountCode(e.target.value)}
            style={{ width: '100%', height: 38, fontSize: 14 }}
          >
            {cashAccounts.length === 0 && <option value="1112">1112 · ธนาคาร</option>}
            {cashAccounts.map((a) => (
              <option key={a.code} value={a.code}>
                {a.code} · {a.nameTh}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label style={modalLabel}>ใบแจ้งหนี้คงค้าง</label>
        <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 36 }} />
                <th>เลขที่ใบแจ้งหนี้</th>
                <th>กำหนดชำระ</th>
                <th className="num">คงค้าง</th>
                <th className="num" style={{ width: 140 }}>
                  จำนวนที่รับชำระ
                </th>
                <th style={{ width: 150 }}>ประเภทเงินได้ (หัก ณ ที่จ่าย)</th>
                <th className="num" style={{ width: 110 }}>
                  ภาษีหัก ณ ที่จ่าย
                </th>
              </tr>
            </thead>
            <tbody>
              {openInvoices.length === 0 && (
                <tr>
                  <td colSpan={7} className="muted" style={{ textAlign: 'center', padding: 16 }}>
                    ไม่พบใบแจ้งหนี้คงค้าง
                  </td>
                </tr>
              )}
              {openInvoices.map((inv) => {
                const out = outstandingById[inv.id] ?? 0;
                const on = !!ticked[inv.id];
                const whtRow = wht[inv.id];
                return (
                  <tr key={inv.id}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`select-${inv.voucherNo}`}
                        checked={on}
                        onChange={() => toggle(inv.id)}
                      />
                    </td>
                    <td className="code">{inv.voucherNo}</td>
                    <td>{String(inv.dueDate).slice(0, 10)}</td>
                    <td className="num tnum">{fmt(out, 2)}</td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`amount-${inv.voucherNo}`}
                        value={on ? (amounts[inv.id] ?? 0) : ''}
                        disabled={!on}
                        min={0}
                        max={out}
                        step={0.01}
                        onChange={(e) => setAmount(inv.id, Number(e.target.value))}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                    <td>
                      <select
                        className="select"
                        aria-label={`wht-income-${inv.voucherNo}`}
                        value={whtRow?.incomeType ?? ''}
                        disabled={!on}
                        onChange={(e) => setIncomeType(inv, e.target.value)}
                        style={{ width: '100%', height: 32, fontSize: 13 }}
                      >
                        <option value="">ไม่มี</option>
                        {whtIncomeOptions.map((o) => (
                          <option key={o.value} value={o.value}>
                            {o.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">
                      <input
                        type="number"
                        className="input tnum"
                        aria-label={`wht-amount-${inv.voucherNo}`}
                        value={on && whtRow ? whtRow.amount : ''}
                        disabled={!on || !whtRow}
                        min={0}
                        step={0.01}
                        onChange={(e) => setWhtAmount(inv.id, Number(e.target.value))}
                        style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right' }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <label style={modalLabel}>คำอธิบาย (optional)</label>
        <input
          className="input"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="หมายเหตุ / เลขที่อ้างอิงเช็ค"
          style={{ width: '100%', height: 38, fontSize: 14 }}
        />
      </div>

      {whtTotal > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={modalLabel}>เลขที่หนังสือรับรองหัก ณ ที่จ่าย (optional)</label>
            <input
              className="input"
              aria-label="wht-cert-no"
              value={whtCertNo}
              onChange={(e) => setWhtCertNo(e.target.value)}
              placeholder="50 ทวิ เลขที่"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={modalLabel}>วันที่หนังสือรับรอง (optional)</label>
            <input
              type="date"
              className="input"
              aria-label="wht-cert-date"
              value={whtCertDate}
              onChange={(e) => setWhtCertDate(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
        </div>
      )}

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 12px',
          borderRadius: 8,
          background: 'var(--brand-tint)',
          fontSize: 14,
          gap: 16,
        }}
      >
        <span className="muted">จัดสรรรวม ({tickedCount} ใบ)</span>
        <div style={{ display: 'flex', gap: 20, alignItems: 'baseline' }}>
          <span className="muted">
            ยอดรวม{' '}
            <b className="tnum" style={{ fontSize: 15, color: 'var(--text)' }}>
              {fmt(allocated, 2)}
            </b>
          </span>
          <span className="muted">
            หัก ณ ที่จ่าย{' '}
            <b className="tnum" style={{ fontSize: 15, color: 'var(--text)' }}>
              {fmt(whtTotal, 2)}
            </b>
          </span>
          <span>
            เงินสุทธิ{' '}
            <b className="tnum" style={{ fontSize: 16 }}>
              {fmt(netCash, 2)} บาท
            </b>
          </span>
        </div>
      </div>

      {error && <div style={modalError}>{error}</div>}

      <div style={modalFooter}>
        <button type="button" className="btn primary" disabled={disabled} onClick={handleSubmit}>
          <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึกการรับชำระ'}
        </button>
      </div>
    </div>
  );
}

export interface RecordReceiptModalProps {
  companyId: string;
  // `| undefined` (not just optional) so callers may pass an explicit undefined
  // under exactOptionalPropertyTypes.
  presetCustomerId?: string | undefined;
  // Single-invoice preset (per-row "รับชำระ") and/or a multi-invoice preset
  // (bulk "รับชำระ"). Both are normalized to one id list and pre-tick the form.
  presetInvoiceId?: string | undefined;
  presetInvoiceIds?: string[] | undefined;
  onClose: () => void;
}

export function RecordReceiptModal({
  companyId,
  presetCustomerId,
  presetInvoiceId,
  presetInvoiceIds,
  onClose,
}: RecordReceiptModalProps) {
  const [customerId, setCustomerId] = useState<string>(presetCustomerId ?? '');

  // Normalize the single + bulk preset props into one de-duped id list.
  const presetIds = useMemo(
    () =>
      Array.from(
        new Set([...(presetInvoiceId ? [presetInvoiceId] : []), ...(presetInvoiceIds ?? [])]),
      ),
    [presetInvoiceId, presetInvoiceIds],
  );

  const { data: customers = [] } = useCustomers(companyId, true);
  const create = useCreateArReceipt(companyId);

  // Open invoices for the chosen customer — POSTED only, then trimmed to rows
  // that still carry outstanding (a POSTED row can be fully paid pre-PAID flip).
  const { data: invoices = [] } = useArInvoices(
    companyId,
    customerId ? { customerId, status: 'POSTED' } : {},
  );
  const openInvoices = useMemo(
    () =>
      customerId
        ? invoices.filter(
            (r) =>
              toNumber(r.totalAmount) - toNumber(r.paidAmount) - toNumber(r.creditedAmount) > 0,
          )
        : [],
    [invoices, customerId],
  );

  // Cash + bank accounts — codes in the 1110-1119 range (cash equivalents).
  const { data: coa = [] } = useCoa(companyId, true);
  const cashAccounts = useMemo(
    () =>
      coa.filter((c) => c.type === 'ASSET' && c.isActive && c.code >= '1110' && c.code < '1120'),
    [coa],
  );

  const customerLabel = useMemo(() => {
    const m = new Map(customers.map((c) => [c.id, `${c.code} · ${c.nameTh}`]));
    return (id: string) => m.get(id) ?? id;
  }, [customers]);

  const customerOptions = useMemo(
    () => customers.map((c) => ({ value: c.id, label: `${c.code} · ${c.nameTh}` })),
    [customers],
  );

  async function handleSubmit(payload: CreateArReceiptInput) {
    // Parse the form's wire-input into the validated output shape (fills the
    // allocation whtAmount default) before the POST; the form keeps emitting the
    // lean input so a no-WHT allocation stays {invoiceId, amount}.
    const data = CreateArReceiptSchema.parse({ ...payload, customerId });
    await create.mutateAsync({ data });
    onClose();
  }

  // WS8 R2 — standard AppModal (1200px) replaces the bespoke 720px chrome. The
  // customer picker stays a pre-form child; the conditional form/empty-state and
  // error are body children. Container-only — RecordReceiptForm is unchanged.
  return (
    <AppModal title="บันทึกการรับชำระ" onClose={onClose}>
      <div style={{ padding: '0 22px 0' }}>
        <label style={modalLabel}>ลูกค้า</label>
        <SearchSelect
          value={customerId}
          onChange={setCustomerId}
          options={customerOptions}
          getLabel={customerLabel}
          placeholder="— เลือกลูกค้า —"
          disabled={!!presetCustomerId}
        />
      </div>

      {customerId ? (
        <RecordReceiptForm
          key={`${customerId}:${presetIds.join(',')}`}
          openInvoices={openInvoices}
          cashAccounts={cashAccounts}
          onSubmit={(payload) => void handleSubmit(payload)}
          submitting={create.isPending}
          customerId={customerId}
          presetInvoiceIds={presetIds}
        />
      ) : (
        <div style={{ ...modalBody, color: 'var(--text-muted)' }}>
          เลือกลูกค้าเพื่อแสดงใบแจ้งหนี้คงค้าง
        </div>
      )}

      {create.error && (
        <div style={{ ...modalError, padding: '0 22px 16px' }}>
          บันทึกไม่สำเร็จ: {create.error.message}
        </div>
      )}
    </AppModal>
  );
}
